import { auth, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import { logDebugEvent } from "@/services/debugLogs";

export class AuthRequiredError extends Error {
  constructor(message = "Login required for feedback generation") {
    super(message);
    this.name = "AuthRequiredError";
    this.code = "auth-required";
  }
}

export class FeedbackLimitError extends Error {
  constructor(
    message = "No feedback attempts remaining. Upgrade to continue getting feedback.",
    details = null
  ) {
    super(message);
    this.name = "FeedbackLimitError";
    this.code = "feedback-limit";
    this.details = details;
  }
}

export function isAuthRequiredError(error) {
  if (!error) return false;

  const code = String(error.code || "").toLowerCase();
  const message = String(error.message || "").toLowerCase();
  return (
    code === "auth-required" ||
    code === "functions/unauthenticated" ||
    code === "unauthenticated" ||
    message.includes("unauthenticated") ||
    error.name === "AuthRequiredError"
  );
}

export function isFeedbackLimitError(error) {
  if (!error) return false;

  const code = String(error.code || "").toLowerCase();
  const message = String(error.message || "").toLowerCase();

  return (
    code === "feedback-limit" ||
    code === "functions/resource-exhausted" ||
    code === "resource-exhausted" ||
    message.includes("free feedback submissions") ||
    message.includes("feedback attempts remaining") ||
    message.includes("no feedback attempts remaining") ||
    error.name === "FeedbackLimitError"
  );
}

export async function getFeedback(payload) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    logDebugEvent("feedback", "blocked-no-current-user");
    throw new AuthRequiredError(
      "Create a free account to get personalized essay feedback."
    );
  }

  logDebugEvent("feedback", "request-start", {
    uid: currentUser.uid,
    promptId: payload?.prompt?.id || null,
    moduleVersion: payload?.prompt?.moduleVersion || null
  });

  // Ensure the client has a usable ID token before calling protected functions.
  const token = await currentUser.getIdToken(true);
  logDebugEvent("feedback", "token-ready", {
    uid: currentUser.uid,
    tokenLength: token ? token.length : 0
  });

  const fn = httpsCallable(functions, "generateEssayFeedback");
  try {
    const result = await fn(payload);
    logDebugEvent("feedback", "request-success", {
      uid: currentUser.uid,
      overallScore: result?.data?.overallScore ?? null,
      accessFeedbackCount:
        typeof result?.data?.access?.feedbackCount === "number"
          ? result.data.access.feedbackCount
          : null,
      accessFreeFeedbackLimit:
        typeof result?.data?.access?.freeFeedbackLimit === "number"
          ? result.data.access.freeFeedbackLimit
          : null,
      accessRemainingFreeFeedback:
        typeof result?.data?.access?.remainingFreeFeedback === "number"
          ? result.data.access.remainingFreeFeedback
          : null
    });
    return result.data;
  } catch (error) {
    logDebugEvent("feedback", "request-failed", {
      uid: currentUser.uid,
      code: error?.code || null,
      message: error?.message || null,
      details: error?.details || null
    });

    if (isAuthRequiredError(error)) {
      throw new AuthRequiredError(
        "Please log in again to continue and generate personalized feedback."
      );
    }

      if (isFeedbackLimitError(error)) {
        throw new FeedbackLimitError(
          error?.message ||
            "No feedback attempts remaining. Upgrade to continue getting feedback.",
          error?.details || null
        );
      }

    throw error;
  }
}
